import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useExamContext } from "@/contexts/ExamContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useEntitlement } from "@/hooks/useEntitlement";

/**
 * Consumes a `?exam=<slug|all>` query param to scope the user UI to a specific
 * exam. When a real admin hits such a URL, also auto-enables "View as User"
 * impersonation so the page renders as a student would see it.
 *
 * The param is stripped from the URL after being applied so internal navigation
 * doesn't keep re-triggering the effect.
 */
export const ExamDeepLinkHandler = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { availableExams, setSelectedExamSlug, isLoading } = useExamContext();
  const { setViewAsUser, viewAsUser } = useImpersonation();
  const { isRealAdmin, loading: entitlementLoading } = useEntitlement();
  const appliedRef = useRef<string | null>(null);

  const examParam = searchParams.get("exam");

  useEffect(() => {
    if (!examParam) return;
    if (isLoading || entitlementLoading) return;
    if (appliedRef.current === examParam) return;

    appliedRef.current = examParam;

    if (examParam === "all") {
      setSelectedExamSlug(null);
      toast.success("Previewing — All Exams");
    } else {
      const match = availableExams.find((e) => e.slug === examParam);
      if (match) {
        setSelectedExamSlug(match.slug);
        toast.success(`Previewing — ${match.name}`);
      } else {
        setSelectedExamSlug(null);
        toast.warning(`Unknown exam "${examParam}" — showing all exams`);
      }
    }

    if (isRealAdmin && !viewAsUser) {
      setViewAsUser(true);
    }

    // Strip just the `exam` key, preserve other params.
    const next = new URLSearchParams(searchParams);
    next.delete("exam");
    setSearchParams(next, { replace: true });
  }, [
    examParam,
    isLoading,
    entitlementLoading,
    availableExams,
    isRealAdmin,
    viewAsUser,
    setSelectedExamSlug,
    setViewAsUser,
    searchParams,
    setSearchParams,
  ]);

  return null;
};

export default ExamDeepLinkHandler;
